import { getAssetUrl, getExercise, searchExercises } from '@bryllim/workout-guide';
import PRESET_EXERCISES from './exercises';

export const GUIDE_VERSION = '1.0.0';

const ALIASES = {
  'barbell bench press': 'bench-press',
  'incline barbell bench press': 'incline-bench-press',
  'incline dumbbell bench press': 'incline-dumbbell-press',
  'incline dumbbell press': 'incline-dumbbell-press',
  'dips': 'dip',
  'dip': 'dip',
  'tricep dips': 'dip',
  'push ups': 'push-up',
  'pull ups': 'pull-up',
  'chin ups': 'chin-up',
  'face pulls': 'face-pull',
  'dumbbell row': 'one-arm-dumbbell-row',
  'cable row': 'seated-row',
  'dumbbell shoulder press': 'seated-dumbbell-press',
  'barbell curl': 'bicep-curl',
  'dumbbell curl': 'bicep-curl',
  'cable overhead tricep extension': 'overhead-tricep-extension',
  'lunge': 'walking-lunge',
  'lunges': 'walking-lunge',
  'nordic curl': 'nordic-hamstring-curl',
  'ab wheel rollout': 'ab-wheel',
  'sit up': 'decline-sit-up',
  'sit ups': 'decline-sit-up',
  'farmers walk': 'farmer-carry',
  'farmer walk': 'farmer-carry',
  'farmer carry': 'farmer-carry',
  'rdl': 'romanian-deadlift',
  'calf raises': 'calf-raise',
};

const GUIDE_TO_APP = {
  Chest: 'chest',
  Shoulders: 'shoulders',
  'Rear Delts': 'shoulders',
  Biceps: 'biceps',
  Triceps: 'triceps',
  Core: 'abs',
  Quads: 'quads',
  Legs: 'quads',
  Hamstrings: 'hamstrings',
  'Posterior Chain': 'hamstrings',
  Glutes: 'glutes',
  Hips: 'glutes',
  Calves: 'calves',
  Lats: 'back',
  Back: 'back',
  'Lower Back': 'back',
  'Upper Back': 'traps',
  Adductors: 'quads',
};

const APP_TO_GUIDE = {
  chest: ['Chest'],
  shoulders: ['Shoulders', 'Rear Delts'],
  biceps: ['Biceps'],
  triceps: ['Triceps'],
  abs: ['Core'],
  quads: ['Quads'],
  hamstrings: ['Hamstrings'],
  glutes: ['Glutes'],
  calves: ['Calves'],
  back: ['Lats', 'Back'],
  traps: ['Upper Back'],
};

const EQUIPMENT_PREFIX = /^(barbell|dumbbell|ez[- ]bar|machine|cable|smith machine|kettlebell)\s+/;

function normalize(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function slugify(value) {
  return normalize(value).replace(/\s+/g, '-');
}

function tokens(value) {
  return normalize(value).split(' ').filter((part) => part.length > 1);
}

const REVERSE_ALIAS = {};
Object.entries(ALIASES).forEach(([name, slug]) => {
  const preset = PRESET_EXERCISES.find((item) => normalize(item) === name);
  if (preset && !REVERSE_ALIAS[slug]) REVERSE_ALIAS[slug] = preset;
});
PRESET_EXERCISES.forEach((name) => {
  const slug = slugify(name);
  if (getExercise(slug) && !REVERSE_ALIAS[slug]) REVERSE_ALIAS[slug] = name;
});
Object.entries(ALIASES).forEach(([, slug]) => {
  if (REVERSE_ALIAS[slug]) return;
  const preset = PRESET_EXERCISES.find((item) => ALIASES[normalize(item)] === slug);
  if (preset) REVERSE_ALIAS[slug] = preset;
});

function candidateSlugs(name) {
  const normalized = normalize(name);
  const slugs = [slugify(normalized)];
  const stripped = normalized.replace(EQUIPMENT_PREFIX, '');
  if (stripped && stripped !== normalized) slugs.push(slugify(stripped));
  return [...new Set(slugs.filter(Boolean))];
}

function scoreMatch(query, exercise) {
  const qn = normalize(query);
  const en = normalize(exercise.name);
  if (!qn || !en) return 0;
  if (qn === en) return 120;
  if (en.startsWith(qn) || qn.startsWith(en)) return 90;
  const queryTokens = tokens(query);
  if (!queryTokens.length) return 0;
  const hay = new Set(tokens(`${exercise.name} ${exercise.slug.replace(/-/g, ' ')}`));
  const hits = queryTokens.filter((part) => hay.has(part)).length;
  if (hits < queryTokens.length) return 0;
  return 50 + hits * 8 - Math.abs(en.length - qn.length);
}

export function findGuideExercise(name) {
  const normalized = normalize(name);
  if (!normalized) return null;
  const aliased = ALIASES[normalized];
  if (aliased) {
    const hit = getExercise(aliased);
    if (hit) return hit;
  }
  for (const slug of candidateSlugs(name)) {
    const hit = getExercise(slug);
    if (hit) return hit;
  }
  const results = searchExercises(name) || [];
  let best = null;
  let bestScore = 0;
  results.forEach((exercise) => {
    const score = scoreMatch(name, exercise);
    if (score > bestScore) {
      best = exercise;
      bestScore = score;
    }
  });
  return bestScore >= 50 ? best : null;
}

export function getGuideFrames(name) {
  const exercise = typeof name === 'string' ? findGuideExercise(name) : name;
  if (!exercise) return null;
  const frames = [1, 2, 3].map((index) => getAssetUrl(exercise.slug, index, { version: GUIDE_VERSION }));
  if (frames.some((url) => !url)) return null;
  return frames;
}

export function searchGuideExercises(query, limit = 6) {
  if (!query || String(query).trim().length < 2) return [];
  const ranked = (searchExercises(query) || [])
    .map((exercise) => ({ exercise, score: scoreMatch(query, exercise) }))
    .filter((row) => row.score > 0)
    .sort((a, b) => b.score - a.score);
  const seen = new Set();
  const out = [];
  for (const row of ranked) {
    if (seen.has(row.exercise.slug)) continue;
    seen.add(row.exercise.slug);
    out.push(row.exercise);
    if (out.length >= limit) break;
  }
  return out;
}

export function displayNameForGuide(exercise) {
  if (!exercise) return '';
  return REVERSE_ALIAS[exercise.slug] || exercise.name;
}

export function guideMuscleToAppKey(label) {
  return GUIDE_TO_APP[label] || null;
}

export function appMuscleKeysForGuide(exercise) {
  if (!exercise) return [];
  const keys = [];
  [exercise.primaryMuscle, ...(exercise.secondaryMuscles || [])].forEach((label) => {
    const key = guideMuscleToAppKey(label);
    if (key && !keys.includes(key)) keys.push(key);
  });
  return keys;
}

export function googleSearchUrl(name, language = 'en') {
  const suffix = language === 'da' ? ' øvelse teknik' : ' exercise form';
  return `https://www.google.com/search?q=${encodeURIComponent(`${name}${suffix}`)}`;
}

export function catalogExercisesForMuscle(appKey, limit = 8) {
  const muscles = APP_TO_GUIDE[appKey];
  if (!muscles) return [];
  const ranked = [];
  muscles.forEach((primaryMuscle) => {
    (searchExercises('', { primaryMuscle, isStretch: false }) || []).forEach((exercise, index) => {
      ranked.push({ exercise, index, group: primaryMuscle });
    });
  });
  const seen = new Set();
  const out = [];
  for (const row of ranked) {
    if (seen.has(row.exercise.slug) || row.exercise.isStretch) continue;
    seen.add(row.exercise.slug);
    out.push(row.exercise);
    if (out.length >= limit) break;
  }
  return out;
}
